import {
  useIsMutating,
  useMutation,
  UseMutationOptions,
} from '@tanstack/react-query'
import { useInvalidateReviews } from '@tovala/browser-apis-combinedapi'

import {
  createMealReview,
  CreateMealReviewRequest,
  CreateMealReviewResponse,
} from '../service/combinedapi/reviews'

function getCreateMealReviewMutationKey(mealID: number) {
  return ['createMealReview', mealID]
}

export function useCreateMealReview({
  mealID,
  ...opts
}: { mealID: number } & Omit<
  UseMutationOptions<CreateMealReviewResponse, Error, CreateMealReviewRequest>,
  'mutationFn' | 'mutationKey'
>) {
  const { invalidateMealReviewSummaries } = useInvalidateReviews()

  return useMutation({
    ...opts,
    mutationFn: createMealReview,
    mutationKey: getCreateMealReviewMutationKey(mealID),
    onSuccess: (...args) => {
      invalidateMealReviewSummaries(args[1].data.userid)

      opts?.onSuccess?.(...args)
    },
  })
}

export function useIsCreatingMealReview(mealID: number) {
  return (
    useIsMutating({ mutationKey: getCreateMealReviewMutationKey(mealID) }) > 0
  )
}

export interface CreateMealReviewsRequest {
  reviews: CreateMealReviewRequest['data'][]
  userID: number
}

export function useCreateMealReviews({
  onMealReviewCreated,
  ...opts
}: {
  onMealReviewCreated(data: CreateMealReviewRequest['data']): void
} & Omit<
  UseMutationOptions<void, Error, CreateMealReviewsRequest>,
  'mutationFn'
>) {
  const { invalidateMealReviewSummaries } = useInvalidateReviews()

  return useMutation({
    ...opts,
    mutationFn: async ({ reviews }) => {
      const results = await Promise.allSettled(
        reviews.map(async (data) => {
          await createMealReview({ data })
          onMealReviewCreated(data)
        }),
      )

      const failedResult = results.find(
        (result): result is PromiseRejectedResult =>
          result.status === 'rejected',
      )
      if (failedResult) {
        throw failedResult.reason
      }
    },
    onSettled: (...args) => {
      invalidateMealReviewSummaries(args[2].userID)

      opts?.onSettled?.(...args)
    },
  })
}
